import {
  View, TopMenuView, isUnscheduledView, isScheduledView,
} from '../components/scheduling/ViewMenu';

// Pick the sub-view to land on for each side of the Routes page (TIMELINE vs
// SCHEDULED_LIST, UNSCHEDULED_LIST vs UNSCHEDULED_TABLE) based on the user's
// last interaction. Used by the validator's fallback branch and by the
// tab-matches-date rule in chooseTabForDate; keeping these in one place avoids
// drift if the storage keys or defaults ever change.
export const resolveStoredScheduledView = (): View => {
  const stored = localStorage.getItem('selectedScheduledView') as View | null;
  return isScheduledView(stored) ? stored as View : View.TIMELINE;
};

export const resolveStoredUnscheduledView = (): View => {
  const stored = localStorage.getItem('selectedUnscheduledView') as View | null;
  return isUnscheduledView(stored) ? stored as View : View.UNSCHEDULED_LIST;
};

// The views `?view=` is allowed to hold — the five the view selector offers.
// ORDER_DETAILS and ROUTE_DETAILS are also View members but name the right
// details drawer, which selection drives rather than the URL, so they must
// never be written through.
const PAGE_VIEWS: View[] = [
  View.MAP,
  View.TIMELINE,
  View.SCHEDULED_LIST,
  View.UNSCHEDULED_LIST,
  View.UNSCHEDULED_TABLE,
];

export interface ViewResolutionOptions {
  // False for organizations whose Routes page has no unscheduled order tray
  // (synced workspaces, and the orders-first experiment's treatment arm). Every
  // request for an unscheduled view then resolves to the stored Scheduled
  // sub-view instead. See ui.canViewUnscheduledViews.
  allowUnscheduledViews: boolean;
}

// Resolves a view *request* into the view to put in the URL. A TopMenuView asks
// for a side of the page without caring which sub-view, and lands on the one
// last used there; a concrete View is taken as-is. Anything unrecognised — or
// an unscheduled view an organization can't have — falls back rather than being
// written through.
const getSelectedView = (
  view: View | TopMenuView,
  { allowUnscheduledViews }: ViewResolutionOptions,
): View => {
  if (view === TopMenuView.SCHEDULED) return resolveStoredScheduledView();

  if (view === TopMenuView.UNSCHEDULED) {
    return allowUnscheduledViews ? resolveStoredUnscheduledView() : resolveStoredScheduledView();
  }

  // Both TopMenuView members are ruled out above, so `view` is a concrete View from here down.
  if (isUnscheduledView(view) && !allowUnscheduledViews) return resolveStoredScheduledView();

  return PAGE_VIEWS.includes(view) ? view : View.MAP;
};

export default getSelectedView;
